import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";

import { router } from "./App";
import { AppProviders } from "@/core/providers";
import { isMobileSync, isStandaloneSync } from "@/core/hooks";

import { initI18n } from "@/core/i18n";
import { appliquerLaMarque } from "@dozka/ui";
import { lireLeParametreDurl } from "@/core/demo";
import "./styles/globals.css";

// La marque du DOMAINE (app.rishfans.com = RishFans) : titre, icônes, police.
// Avant le premier render, pour qu'aucun écran ne s'affiche sous la mauvaise.
appliquerLaMarque(document);

// Self-heal PWA : ce module ne s'exécute que si le bundle JS a chargé. On
// efface donc le flag posé par le script inline de index.html (cf.
// "pwa-stale-reload") pour ré-autoriser une future tentative de récupération
// au prochain bundle mort. Sans ce reset, le garde anti-boucle resterait armé.
try {
  sessionStorage.removeItem("pwa-stale-reload");
} catch {
  /* sessionStorage indispo (mode privé strict) — pas grave */
}

// 🎬 `?demo=1` / `?demo=0` — le mode démo se pose AVANT le premier render :
// les premières requêtes partent dans la foulée et doivent déjà savoir si
// elles vont sur Cyrano ou sur le monde de démonstration. Cf `core/demo/`.
lireLeParametreDurl();

// Stamp `data-mobile="true"` sur <html> avant le premier render quand on est
// sur un vrai mobile (user-agent + breakpoint fallback). Permet aux CSS
// `[data-mobile="true"] .X` de s'appliquer des le premier paint, sans flash
// du layout desktop. Pas reactif : un user mobile reste mobile pour la session.
if (typeof document !== "undefined" && isMobileSync()) {
  document.documentElement.dataset.mobile = "true";
}

// Stamp `data-standalone="true"` quand l'app tourne en mode home-screen
// (PWA ajoutee a l'ecran d'accueil) plutot que dans le navigateur.
if (typeof document !== "undefined" && isStandaloneSync()) {
  document.documentElement.dataset.standalone = "true";
}

// Desktop OAuth handoff : quand le browser SYSTEME atterrit sur
// /auth/desktop-callback?bootstrap=<token>, on hand-off le token vers
// l'app Tauri via dozka://. Tauri va ensuite call /api/auth/bootstrap
// dans son webview pour set son propre cookie de session.
//
// On hand-off UNIQUEMENT quand ?bootstrap=... est present — sinon ça veut
// dire qu'on est dans le webview Tauri lui-meme apres dozka:// dispatch
// (Tauri navigue webview vers /api/auth/bootstrap directement, pas via
// /auth/desktop-callback). Le check `__TAURI_INTERNALS__` evite de
// rebondir si on est deja dans Tauri pour une raison quelconque.
const isOnDesktopCallback =
  typeof window !== "undefined" &&
  window.location.pathname === "/auth/desktop-callback";
const hasBootstrapParam =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("bootstrap");
const isInTauri =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

if (isOnDesktopCallback && hasBootstrapParam && !isInTauri) {
  window.location.replace(`dozka://callback${window.location.search}`);
} else {
  const container = document.getElementById("root");
  if (!container) throw new Error("Cockpit: #root not found in index.html");

  const root = createRoot(container);
  const render = () =>
    root.render(
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>,
    );

  // On attend que la langue détectée soit chargée (locales lazy, cf
  // `@/core/i18n`) AVANT le 1er render pour éviter un flash de clés brutes.
  // `.finally` (et non `.then`) : si le fetch de la locale échoue, on rend
  // quand même — i18next retombe alors sur les clés / le fallback `en`.
  void initI18n().finally(render);
}
